import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "classnames";

import { COLOR_SWATCHES, normalizeHexInput } from "utils/background";
import { ACCEPT_ATTR, validateBackgroundFile } from "utils/backgroundImageFile";

import { StyledBackgroundSettings } from "./StyledBackgroundSettings";

const MODES = [
  { key: "default", label: "background.default" },
  { key: "color", label: "background.color" },
  { key: "image", label: "background.image" },
];

// ScanKit 編輯頁的「背景」面板（受控元件）：value 是 utils/background 的編輯狀態，
// 每次變動以 onChange(部分欄位) 回傳；選的圖片只做本機預覽，按「儲存」才上傳
const BackgroundSettings = ({ value, onChange }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [hexText, setHexText] = useState(value.color);
  const [hexInvalid, setHexInvalid] = useState(false);
  const [fileError, setFileError] = useState("");

  useEffect(() => {
    setHexText(value.color);
    setHexInvalid(false);
  }, [value.color]);

  const handleHexChange = (e) => {
    const text = e.target.value;
    setHexText(text);
    const normalized = normalizeHexInput(text);
    setHexInvalid(!normalized);
    if (normalized) onChange({ color: normalized, mode: "color" });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    // 清掉 value，同一個檔案再選一次也會觸發 onChange
    e.target.value = "";
    if (!file) return;
    if (validateBackgroundFile(file)) {
      setFileError(t("background.invalidType"));
      return;
    }
    setFileError("");
    if (value.previewUrl) URL.revokeObjectURL(value.previewUrl);
    onChange({ mode: "image", pendingFile: file, previewUrl: URL.createObjectURL(file) });
  };

  const imageUrl = value.previewUrl || value.bgImage;

  return (
    <StyledBackgroundSettings>
      <div className="panelTitle">{t("background.title")}</div>
      <div className="modes" role="radiogroup" aria-label={t("background.title")}>
        {MODES.map((mode) => (
          <button
            key={mode.key}
            type="button"
            role="radio"
            aria-checked={value.mode === mode.key}
            className={clsx("mode", { active: value.mode === mode.key })}
            onClick={() => onChange({ mode: mode.key })}
          >
            {t(mode.label)}
          </button>
        ))}
      </div>

      {value.mode === "default" && <div className="hint">{t("background.defaultHint")}</div>}

      {value.mode === "color" && (
        <>
          <div className="label">{t("background.swatches")}</div>
          <div className="swatches">
            {COLOR_SWATCHES.map((color) => (
              <button
                key={color}
                type="button"
                className={clsx("swatch", { active: value.color === color })}
                style={{ background: color }}
                aria-label={color}
                aria-pressed={value.color === color}
                onClick={() => onChange({ color, mode: "color" })}
              />
            ))}
          </div>
          <div className="label">{t("background.customColor")}</div>
          <div className="customColor">
            <input
              type="color"
              value={value.color}
              aria-label={t("background.customColor")}
              onChange={(e) => onChange({ color: e.target.value.toUpperCase(), mode: "color" })}
            />
            <input
              type="text"
              value={hexText}
              maxLength={7}
              spellCheck={false}
              className={clsx({ invalid: hexInvalid })}
              aria-invalid={hexInvalid}
              aria-label={t("background.customColor")}
              onChange={handleHexChange}
            />
          </div>
          {hexInvalid && <div className="error">{t("background.hexInvalid")}</div>}
        </>
      )}

      {value.mode === "image" && (
        <>
          <div
            className="imagePreview"
            style={
              imageUrl ? { backgroundImage: `url("${imageUrl.replace(/"/g, '\\"')}")` } : undefined
            }
          >
            {!imageUrl && t("background.noImage")}
          </div>
          <button
            type="button"
            className="uploadButton"
            onClick={() => fileInputRef.current?.click()}
          >
            {imageUrl ? t("background.replace") : t("background.upload")}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTR}
            className="visuallyHidden"
            tabIndex={-1}
            aria-hidden="true"
            onChange={handleFileChange}
          />
          {fileError ? (
            <div className="error">{fileError}</div>
          ) : (
            <div className="hint">{t("background.imageHint")}</div>
          )}
        </>
      )}

      <div className="hint scankitNote">{t("background.scankitNote")}</div>
    </StyledBackgroundSettings>
  );
};

export default BackgroundSettings;
